const normalizeLanguage = (value?: string | null): string =>
  String(value || "").trim().slice(0, 2).toLowerCase() || "es";

const MENUS_CACHE_SCHEMA_KEY = "menusCacheSchema";
const MENUS_CACHE_SCHEMA = "2";

function ensureCurrentMenusCacheSchema() {
  try {
    if (localStorage.getItem(MENUS_CACHE_SCHEMA_KEY) === MENUS_CACHE_SCHEMA) return;
    clearAllMenusStorage();
    localStorage.setItem(MENUS_CACHE_SCHEMA_KEY, MENUS_CACHE_SCHEMA);
  } catch {
    // Storage is an optimization; network loading remains authoritative.
  }
}

const menusKey = (language: string) => `menus.${normalizeLanguage(language)}`;
const menusChunkCountKey = (language: string) => `menus_chunk_count.${normalizeLanguage(language)}`;
const menusChunkKey = (language: string, index: number) => `menus_chunk_${normalizeLanguage(language)}_${index}`;
const menusUpdatedAtKey = (language: string) => `menusUpdatedAt.${normalizeLanguage(language)}`;

function clearChunkedMenus(language: string) {
  try {
    const count = parseInt(localStorage.getItem(menusChunkCountKey(language)) || "0", 10);
    if (count > 0) {
      for (let i = 0; i < count; i += 1) localStorage.removeItem(menusChunkKey(language, i));
      localStorage.removeItem(menusChunkCountKey(language));
    }
  } catch {
    // ignore cleanup errors
  }
}

export function saveMenusToStorage(menus: any[], language: string) {
  ensureCurrentMenusCacheSchema();
  const normalizedLanguage = normalizeLanguage(language);
  const json = JSON.stringify(menus || []);

  clearChunkedMenus(normalizedLanguage);

  try {
    localStorage.setItem(menusKey(normalizedLanguage), json);
  } catch {
    const chunkSize = 500_000;
    const chunks: string[] = [];
    for (let i = 0; i < json.length; i += chunkSize) chunks.push(json.slice(i, i + chunkSize));
    chunks.forEach((chunk, index) => localStorage.setItem(menusChunkKey(normalizedLanguage, index), chunk));
    localStorage.setItem(menusChunkCountKey(normalizedLanguage), String(chunks.length));
    localStorage.removeItem(menusKey(normalizedLanguage));
  }

  localStorage.setItem(menusUpdatedAtKey(normalizedLanguage), String(Date.now()));
  window.dispatchEvent(new CustomEvent("menus:updated", { detail: { language: normalizedLanguage } }));
}

export function readMenusFromStorage(language: string): string | null {
  ensureCurrentMenusCacheSchema();
  const normalizedLanguage = normalizeLanguage(language);
  const plain = localStorage.getItem(menusKey(normalizedLanguage));
  if (plain) return plain;

  const count = parseInt(localStorage.getItem(menusChunkCountKey(normalizedLanguage)) || "0", 10);
  if (count > 0) {
    let json = "";
    for (let i = 0; i < count; i += 1) json += localStorage.getItem(menusChunkKey(normalizedLanguage, i)) || "";
    return json || null;
  }

  return null;
}

export function readMenusUpdatedAt(language: string): number {
  ensureCurrentMenusCacheSchema();
  const normalizedLanguage = normalizeLanguage(language);
  return Number(localStorage.getItem(menusUpdatedAtKey(normalizedLanguage)) || 0);
}

export function clearMenusStorage(language: string) {
  const normalizedLanguage = normalizeLanguage(language);
  try {
    clearChunkedMenus(normalizedLanguage);
    localStorage.removeItem(menusKey(normalizedLanguage));
    localStorage.removeItem(menusUpdatedAtKey(normalizedLanguage));
  } catch {
    // ignore cleanup errors
  }
}

export function clearAllMenusStorage() {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (
        key === "menus" ||
        key === "menus_chunk_count" ||
        key === "menusUpdatedAt" ||
        key.startsWith("menus_chunk_") ||
        key.startsWith("menus.") ||
        key.startsWith("menus_chunk_count.") ||
        key.startsWith("menus_chunk_") ||
        key.startsWith("menusUpdatedAt.")
      ) {
        localStorage.removeItem(key);
      }
    });
  } catch {
    // ignore storage errors
  }
}
